import * as moment from 'moment';
import { getUnitLabel } from '..';

import { IApplicationConfiguration, IDatePickerDates, IFrequency, IStringDateRange, TimeUnit } from '../../models';
import { IDateAlignmentHelperService } from './dateAlignmentHelper.service';
import { toDuration } from './toDuration';

export interface IDateRangePickerService {
    isAligned(datePickerDates: IDatePickerDates, frequency: IFrequency, appConfiguration: IApplicationConfiguration): boolean;
    /**
     * Forces alignment of dates according to given frequency.
     * Watch out that picker is returning end date at 23:59:59 if timepick is disabled.
     * @param dates the orignal range from the date range picker.
     * @param freq The frequency at whic range should be aligned
     * @param config The applicaiton configuration
     * @param alignBeginOnPrevious allow to realign begin date on previous to have englobing range
     */
    enforceAlign(dates: IDatePickerDates, freq: IFrequency, config: IApplicationConfiguration, alignBeginOnPrevious?: boolean): IDatePickerDates;
    enforceAlignString(dates: IStringDateRange, freq: IFrequency, config: IApplicationConfiguration, alignBeginOnPrevious?: boolean): IStringDateRange;
}

export class DateRangePickerService implements IDateRangePickerService {

    static serviceId = 'dateRangePickerService';
    static $inject = ['dateAlignmentHelperService'];

    constructor(private _dateAlignmentHelperService: IDateAlignmentHelperService) {
    }

    isAligned(dates: IDatePickerDates, frequency: IFrequency, appConfiguration: IApplicationConfiguration): boolean {
        const result = this.enforceAlign(dates, frequency, appConfiguration, false);
        const diffBegin = dates.startDate.diff(result.startDate, 's');
        const diffEnd = dates.endDate.diff(result.endDate, 's');
        const isAligned = diffBegin === 0 && diffEnd === 0;
        return isAligned;
    }

    /**
     * Forces alignment of dates according to given frequency.
     * Watch out that picker is returning end date at 23:59:59 if timepick is disabled.
     * @param dates the orignal range from the date range picker.
     * @param freq The frequency at which range should be aligned
     * @param config The application configuration
     */
    enforceAlign(dates: IDatePickerDates, freq: IFrequency, config: IApplicationConfiguration, alignBeginOnPrevious?: boolean): IDatePickerDates {
        const helper = this._dateAlignmentHelperService;

        // Reset seconds and milliseconds
        let begin = dates.startDate.clone().seconds(0).milliseconds(0);
        let end = dates.endDate.clone().seconds(0).milliseconds(0);

        // Then align on desired frequency
        const { timeZone } = config;
        if (alignBeginOnPrevious) {
            // #47752 : First day of the period, alignment must be next if not aligned
            const duration = getUnitLabel(freq);
            if (freq.intervalTimeUnit >= TimeUnit.Day &&
                ((freq.intervalTimeUnit === TimeUnit.Week && begin.day() === config.weekBeginDay)
                    || (freq.intervalTimeUnit !== TimeUnit.Week && moment(begin).startOf(duration as any).date() === moment(begin).date()))) {
                if (!this.isAligned(dates, freq, config)) {
                    begin = helper.next(begin, freq, config.beginDayTime, config.weekBeginDay, timeZone);
                }
            } else {
                // For what I saw during #46820, alignBeginOnPrevious is only true in groups sub states;
                // in that case, we don't want to realign if already aligned (or not in the case I solved at least)
                begin = helper.previous(begin, freq, config.beginDayTime, config.weekBeginDay, timeZone, false);
            }
        } else {
            begin = helper.next(begin, freq, config.beginDayTime, config.weekBeginDay, timeZone);
        }
        end = helper.next(end, freq, config.beginDayTime, config.weekBeginDay, timeZone);

        return {
            startDate: begin,
            endDate: end
        };
    }

    enforceAlignString(dates: IStringDateRange, freq: IFrequency, config: IApplicationConfiguration, alignBeginOnPrevious?: boolean): IStringDateRange {
        const momentDates: IDatePickerDates = {
            startDate: moment(dates.startDate),
            endDate: moment(dates.endDate)
        };
        const result = this.enforceAlign(momentDates, freq, config, alignBeginOnPrevious);
        return {
            startDate: result.startDate.toISOString(),
            endDate: result.endDate.toISOString()
        };
    }
}